import AppIcon from "common/app-icon";
import * as React from "react";
import { useSelector } from "react-redux";
import { styled } from "sharedUI/styles";
import { getCurrentAppAndState, RootState } from "store/types";
import PreviewAsModal from "./private/preview-as-modal";
import { url as gravatar } from "gravatar";
import Img from "webapp/ui/smart/img/img";
import { withUserProfiles } from "./private/user-profile-row-provider";
import classNames from "classnames";
import { useAppID } from "hooks/use-app-id";
import { useAppEnvironment } from "hooks/use-app-environment";
import { UserProfileRow } from "common/components/user-profile";

const PreviewAsWidgetStyle = styled.div`
    display: flex;
    align-items: center;
    min-width: 100px;
    max-width: 20vw;
    height: 32px;
    padding: 6px;
    border-radius: 8px;
    font-size: 12px;
    transition: background-color ${p => p.theme.transitionBase};
    cursor: pointer;

    .layout & {
        max-width: 220px;
    }

    &.open,
    :hover {
        background-color: ${p => p.theme.n200A};
    }

    .icon-area {
        width: 20px;
        height: 20px;
        margin-right: 4px;
        flex-shrink: 0;
        border-radius: 100px;
        overflow: hidden;
        box-shadow: ${p => p.theme.shadow1};

        .icon {
            color: ${p => p.theme.n500};
            width: 20px;
            height: 20px;
            object-fit: cover;
        }
    }

    .label-area {
        flex-shrink: 1;
        min-width: 15px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        color: ${p => p.theme.textPale};

        .label-name {
            color: ${p => p.theme.textBase};
        }
    }

    .caret-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 16px;
        height: 16px;
        margin-left: 2px;
        flex-shrink: 0;
    }

    .caret {
        color: ${p => p.theme.n700A};
    }
`;

interface Props {
    readonly className?: string;
    readonly userProfiles: readonly UserProfileRow[];
}

const PreviewAsWidgetImpl: React.FunctionComponent<Props> = p => {
    const { className, userProfiles } = p;

    const [showMenu, setShowMenu] = React.useState(false);
    // terrible hack to force update
    const [update, setUpdate] = React.useState(0);

    const appID = useAppID();
    const app = useSelector((state: RootState) => getCurrentAppAndState(appID ?? undefined, state.apps));

    const previewTarget = React.useRef<HTMLDivElement | null>(null);

    const appEnvironment = useAppEnvironment(appID);
    const authenticator = appEnvironment?.authenticator;

    const lastUser = authenticator?.virtualEmail;
    const onAuthenticatorChanged = React.useCallback(
        (_: string | undefined, _2: string | undefined, newVirtualEmail: string | undefined) => {
            if (newVirtualEmail !== lastUser) {
                setUpdate(update + 1);
            }
        },
        [lastUser, update]
    );

    React.useEffect(() => {
        if (authenticator === undefined) return;

        authenticator.addCallback(onAuthenticatorChanged);
        return () => {
            authenticator.removeCallback(onAuthenticatorChanged);
        };
    }, [authenticator, onAuthenticatorChanged]);

    if (app === undefined || authenticator === undefined) return null;

    const virtualEmail = authenticator.virtualEmail;

    const icon = <AppIcon size={20} icon="avatarEmpty" />;

    const user = userProfiles.find(x => x.email.toLowerCase() === virtualEmail);

    const displayName = user?.name ?? virtualEmail;

    return (
        <PreviewAsWidgetStyle
            ref={previewTarget}
            className={classNames(className, showMenu && "open")}
            onClick={() => setShowMenu(true)}>
            <div className="icon-area">
                <Img
                    altBehavior="both"
                    className="icon"
                    alternate={icon}
                    alt=""
                    src={
                        user?.imageURL ??
                        gravatar(virtualEmail ?? "", {
                            default: "404",
                        })
                    }
                />
            </div>
            <div className="label-area">
                <span>Viewing as </span>
                <span className="label-name">{displayName === undefined ? "anyone" : displayName}</span>
            </div>
            <div className="caret-container">
                <AppIcon icon={"caret"} size={8} className={"caret"} />
            </div>
            {showMenu && (
                <PreviewAsModal
                    authenticator={authenticator}
                    targetRef={previewTarget}
                    onClose={() => setShowMenu(false)}
                />
            )}
        </PreviewAsWidgetStyle>
    );
};

const PreviewAsWidget = withUserProfiles(PreviewAsWidgetImpl);
export default PreviewAsWidget;
